import { debounce } from '@theme/utils';

class SectionFaqs extends HTMLElement {
  constructor() {
    super();

    this.search = this.querySelector('[js-faqs="search"]');
    this.triggers = [...this.querySelectorAll('[js-faqs="trigger"]')];
    this.contents = [...this.querySelectorAll('[js-faqs="content"]')];
    this.noResults = this.querySelector('[js-faqs="no-results"]');
    this.groupWrapper = this.querySelector('.faqs__group-wrapper');

    // Fuzzy search configuration
    this.fuzzyThreshold = 0.6; // Minimum score to be considered a match (0-1)
  }

  connectedCallback() {
    if (!this.triggers || !this.contents) {
      return;
    }

    this.triggers.forEach(group => {
      group.addEventListener('click', this.handleGroupClick.bind(this));
    });

    this.search.addEventListener('input', debounce(this.handleSearchChange.bind(this), 300));
  }

  handleGroupClick(event) {
    const groupId = event.target.dataset.groupId;

    if (!groupId) {
      return;
    }

    this.triggers.forEach(group => {
      group.classList.toggle('is-active', group.dataset.groupId === groupId);
    });

    this.contents.forEach(content => {
      content.classList.toggle('is-active', content.dataset.groupId === groupId);
    });
  }

  handleSearchChange() {
    const searchValue = this.search.value.toLowerCase().trim();

    // Reset to default state when search is empty
    if (!searchValue) {
      this.resetToDefault();
      return;
    }

    // Hide group triggers during search
    if (this.groupWrapper) {
      this.groupWrapper.style.display = 'none';
    }

    let hasResults = false;
    const results = [];

    // Search through all groups and FAQs with fuzzy matching
    this.contents.forEach(content => {
      const faqs = [...content.querySelectorAll('[js-faq-item]')];

      faqs.forEach(faq => {
        const question = faq.dataset.faqQuestion?.toLowerCase() || '';
        const answer = faq.dataset.faqAnswer?.toLowerCase() || '';

        // Calculate fuzzy match scores
        const questionScore = this.fuzzyMatch(searchValue, question);
        const answerScore = this.fuzzyMatch(searchValue, answer);
        const maxScore = Math.max(questionScore, answerScore);

        // Also check for exact substring matches (higher priority)
        const hasExactMatch = question.includes(searchValue) || answer.includes(searchValue);

        if (hasExactMatch || maxScore >= this.fuzzyThreshold) {
          results.push({
            faq,
            score: hasExactMatch ? 1 : maxScore,
            matchedIn: questionScore > answerScore ? 'question' : 'answer'
          });
        }
      });
    });

    // Sort results by score (highest first)
    results.sort((a, b) => b.score - a.score);

    // Display results
    if (results.length > 0) {
      hasResults = true;

      // Hide all FAQs first
      this.contents.forEach(content => {
        const faqs = [...content.querySelectorAll('[js-faq-item]')];
        faqs.forEach(faq => {
          faq.style.display = 'none';
          this.removeHighlight(faq);
        });
      });

      // Show and highlight matched FAQs
      results.forEach(result => {
        result.faq.style.display = '';
        this.highlightText(result.faq, searchValue);

        // Open the accordion if it matches
        const details = result.faq.querySelector('details');
        if (details) {
          details.setAttribute('open', '');
        }

        // Ensure parent group is visible
        const parentContent = result.faq.closest('[js-faqs="content"]');
        if (parentContent) {
          parentContent.classList.add('is-active');
        }
      });
    }

    // Show/hide no results message
    if (this.noResults) {
      this.noResults.style.display = hasResults ? 'none' : 'block';
    }
  }

  /**
   * Fuzzy match algorithm using Levenshtein distance
   * Returns a score between 0 and 1 (1 being perfect match)
   */
  fuzzyMatch(pattern, text) {
    // If pattern is empty, no match
    if (!pattern) return 0;

    // If text is empty, no match
    if (!text) return 0;

    // Exact match gets highest score
    if (text === pattern) return 1;

    // Check if pattern exists as substring
    if (text.includes(pattern)) return 0.95;

    // Split pattern into words for better matching
    const patternWords = pattern.split(/\s+/);
    const textWords = text.split(/\s+/);

    // Check if all pattern words exist in text (word-level matching)
    const allWordsMatch = patternWords.every(word =>
      textWords.some(textWord =>
        textWord.includes(word) || this.levenshteinDistance(word, textWord) <= Math.ceil(word.length * 0.3)
      )
    );

    if (allWordsMatch) return 0.85;

    // Calculate character-level fuzzy match
    const distance = this.levenshteinDistance(pattern, text);
    const maxLength = Math.max(pattern.length, text.length);
    const similarity = 1 - (distance / maxLength);

    // Boost score if pattern appears at the start of text
    if (text.startsWith(pattern.substring(0, Math.min(3, pattern.length)))) {
      return Math.min(1, similarity + 0.2);
    }

    return similarity;
  }

  /**
   * Calculate Levenshtein distance between two strings
   * Returns the minimum number of edits needed to transform one string into another
   */
  levenshteinDistance(str1, str2) {
    const len1 = str1.length;
    const len2 = str2.length;
    const matrix = [];

    // Initialize matrix
    for (let i = 0; i <= len1; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= len2; j++) {
      matrix[0][j] = j;
    }

    // Fill matrix
    for (let i = 1; i <= len1; i++) {
      for (let j = 1; j <= len2; j++) {
        const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,      // deletion
          matrix[i][j - 1] + 1,      // insertion
          matrix[i - 1][j - 1] + cost // substitution
        );
      }
    }

    return matrix[len1][len2];
  }

  highlightText(faq, searchValue) {
    const question = faq.querySelector('.faqs__faq-question span');
    const answer = faq.querySelector('.faqs__faq-answer');

    if (question) {
      const originalText = question.dataset.originalText || question.textContent;
      if (!question.dataset.originalText) {
        question.dataset.originalText = originalText;
      }
      question.innerHTML = this.wrapMatches(originalText, searchValue);
    }

    if (answer) {
      const originalHtml = answer.dataset.originalHtml || answer.innerHTML;
      if (!answer.dataset.originalHtml) {
        answer.dataset.originalHtml = originalHtml;
      }
      const textContent = answer.textContent;
      if (textContent.toLowerCase().includes(searchValue)) {
        answer.innerHTML = this.wrapMatches(textContent, searchValue);
      }
    }
  }

  removeHighlight(faq) {
    const question = faq.querySelector('.faqs__faq-question span');
    const answer = faq.querySelector('.faqs__faq-answer');

    if (question && question.dataset.originalText) {
      question.textContent = question.dataset.originalText;
    }

    if (answer && answer.dataset.originalHtml) {
      answer.innerHTML = answer.dataset.originalHtml;
    }
  }

  wrapMatches(text, searchValue) {
    const regex = new RegExp(`(${this.escapeRegex(searchValue)})`, 'gi');
    return text.replace(regex, '<mark class="faqs__highlight">$1</mark>');
  }

  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  resetToDefault() {
    // Show group triggers
    if (this.groupWrapper) {
      this.groupWrapper.style.display = '';
    }

    // Reset to first group active
    this.contents.forEach((content, index) => {
      content.classList.toggle('is-active', index === 0);

      // Show all FAQs and remove highlights
      const faqs = [...content.querySelectorAll('[js-faq-item]')];
      faqs.forEach(faq => {
        faq.style.display = '';
        this.removeHighlight(faq);

        // Close accordions except first one in first group
        const details = faq.querySelector('details');
        if (details && !(index === 0 && faqs.indexOf(faq) === 0)) {
          details.removeAttribute('open');
        }
      });
    });

    this.triggers.forEach((trigger, index) => {
      trigger.classList.toggle('is-active', index === 0);
    });

    // Hide no results message
    if (this.noResults) {
      this.noResults.style.display = 'none';
    }
  }
}

customElements.define('section-faqs', SectionFaqs);

